import React, { useCallback, useMemo } from 'react';

import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { Stories } from '@edu-frontend/modules/src/features/showStories';
import { Story, StoryCard } from '@edu-frontend/modules/src/entities/Story';
import { useSelector } from 'react-redux';
import { useGetTeacherStories } from 'common.components/teacher-stories/queries';
import { getCurrentClass } from 'common.components/user/selectors';
import { getTeamIdFromPath } from 'utils/path';
import { getCurrentRole, getUserId } from 'platform-components/src/components/user/selectors';
import { getSubjects } from 'store/courses/selectors';

import ym from 'react-yandex-metrika';
import { useIntl } from 'platform-components';

interface TeacherStoriesProps extends React.PropsWithChildren {
    className?: string;
}

// eslint-disable-next-line complexity
export const TeacherStories = ({ className }: TeacherStoriesProps) => {
    const intl = useIntl();
    const classId = getTeamIdFromPath() || null;
    const team = useSelector((store) => getCurrentClass(store, { classId: classId }));
    const userId = useSelector(getUserId);
    const userRole = useSelector(getCurrentRole);
    const subjects = (useSelector((storeState) => getSubjects(storeState, { classId })) ?? []).map(
        ({ subject }) => subject
    );

    const grade = team?.level;

    const { data: storiesFromCms = [], isLoading } = useGetTeacherStories({
        subjects,
        grades: grade ? [grade] : [],
    });
    const mappedStoriesFromCms: Story[] = useMemo(() => {
        return (storiesFromCms ?? []).map((storyFromCms) => {
            return {
                id: storyFromCms.id,
                header: storyFromCms.header,
                description: storyFromCms.description,
                imgSrc: storyFromCms.image?.desktop,
                backgroundColor: storyFromCms.color,
                theme: storyFromCms.theme,
                cards: storyFromCms.slides.map((slide) => ({
                    id: slide.id,
                    header: slide.header,
                    description: slide.description,
                    imgSrc: slide.image,
                    theme: slide.theme,
                    action: {
                        type: slide.action_type ?? 'link',
                        data: {
                            title: slide.action_data?.title as string,
                            url: slide.action_data?.url as string,
                        },
                    },
                })),
            };
        });
    }, [storiesFromCms]);

    const onBannerAppear = useCallback(
        (story: Story) => {
            ym('reachGoal', 'story_main_view', {
                user_role: userRole,
                user_id: userId,
                story,
            });
        },
        [userId, userRole]
    );

    const onBannerClick = useCallback(
        (story: Story) => {
            ym('reachGoal', 'story_main_click', {
                user_role: userRole,
                user_id: userId,
                story,
            });
        },
        [userId, userRole]
    );

    const onStoryOpen = useCallback(
        (story: Story) => {
            ym('reachGoal', 'story_open', {
                user_role: userRole,
                user_id: userId,
                story,
            });
        },
        [userId, userRole]
    );

    const onStoryCardOpen = useCallback(
        (story: Story, card: StoryCard) => {
            ym('reachGoal', 'story_card_open', {
                user_role: userRole,
                user_id: userId,
                story,
                card,
            });
        },
        [userId, userRole]
    );

    const onActionButtonClick = useCallback(
        (story: Story, card: StoryCard) => {
            ym('reachGoal', 'story_button_click', {
                user_role: userRole,
                user_id: userId,
                story,
                card,
            });
        },
        [userId, userRole]
    );

    const onStoryClose = useCallback(
        (story: Story, card: StoryCard) => {
            ym('reachGoal', 'story_close', {
                user_role: userRole,
                user_id: userId,
                story,
                card,
            });
        },
        [userId, userRole]
    );

    if (isLoading || mappedStoriesFromCms.length === 0) {
        return null;
    }

    return (
        <Stories
            className={className}
            nextButtonAriaLabel={intl.formatMessage({ id: 'stories.nextButton' })}
            onActionButtonClick={onActionButtonClick}
            onBannerAppear={onBannerAppear}
            onBannerClick={onBannerClick}
            onStoryCardOpen={onStoryCardOpen}
            onStoryClose={onStoryClose}
            onStoryOpen={onStoryOpen}
            prevButtonAriaLabel={intl.formatMessage({ id: 'stories.prevButton' })}
            stories={mappedStoriesFromCms}
            theme={hrLight}
        />
    );
};
